import {
  Badge,
  Box,
  Button,
  Text as CharkaText,
  HStack,
  Input,
  InputGroup,
  Spinner,
  VStack
} from "@chakra-ui/react"
import useDebouncedValue from "@pathwright/ui/src/components/hooks/useDebouncedValue"
import useDidMountEffect from "@pathwright/ui/src/components/hooks/useDidMountEffect"
import Image from "@pathwright/ui/src/components/image/Image"
import PickerDropdown from "@pathwright/ui/src/components/picker/PickerDropdown"
import * as PickerUI from "@pathwright/ui/src/components/picker/ui"
import Text from "@pathwright/ui/src/components/ui/Text"
import { TERTIARY_GRAY } from "@pathwright/ui/src/components/utils/colors"
import { flattenEdges, usePaginator } from "@pathwright/utils"
import Link from "@pathwright/ux/src/components/link/Link"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useSize } from "@pathwright/ux/src/utils/size"
import {
  ResourceFragment,
  ResourceType,
  useResourcesQuery as _useResourcesQuery
} from "@pathwright/graphql-client/src/generated"
import Pathicon from "@pathwright/features/src/modules/pathicon/Pathicon"
import { useEffect, useRef, useState } from "react"
import { useHotkeys, useHotkeysContext } from "react-hotkeys-hook"
import styled from "styled-components"
import LicensorLibraryPickerPrompt from "../../library/picker/LicensorLibraryPickerPrompt"
import { useLicensors } from "../../path/create/Licensors"
import useRandomThemeResourceImage from "../../resource/query/useRandomThemeResourceImage"

// include licensor picker prompts for licensing other resources
const ResourcePickerLicensor = ({
  onOpenStore
}: {
  noResults?: boolean
  onOpenStore: () => void
}) => {
  const licensors = useLicensors()
  // if there is no licensor found, render nothing
  if (!licensors?.length) return null

  // show prompt for each licensor
  return (
    <VStack w="full" mt={2}>
      {licensors.map((licensor) => (
        <LicensorLibraryPickerPrompt
          key={`store-licensor-${licensor.id}`}
          licensorId={licensor.id}
          onOpenStore={onOpenStore}
        />
      ))}
    </VStack>
  )
}

type ResourcePickerProps = {
  resourceType: ResourceType
  onChangeResourceName: (name: string) => void
  preSelectedResource?: ResourceFragment | null
  selectedResource?: ResourceFragment | null
  onSelectResource: (resource: ResourceFragment | null) => void
  onSelectResourceCoverImage: (image: string) => void
  onOpenStore: () => void
  onFocusCohortNameInput: () => void
}

const ResourcePicker = ({
  resourceType,
  onChangeResourceName,
  preSelectedResource,
  selectedResource,
  onSelectResource,
  onSelectResourceCoverImage,
  onOpenStore,
  onFocusCohortNameInput
}: ResourcePickerProps) => {
  // For exactly setting the right padding on the text input for accounting
  // for the input right element.
  const [setInputRightElementNodeRef, inputRightElementWidth] = useSize("width")
  const [showingPicker, setShowingPicker] = useState(false)
  const [focused, setFocused] = useState(false)
  const { t } = useTranslate()
  const [search, debouncedSearch, debouncingSearch, handleDebounceSearch] =
    useDebouncedValue(300)
  const inputRef = useRef<HTMLInputElement>(null)

  function toggleShowingPicker() {
    setShowingPicker((showingPicker) => !showingPicker)
  }

  function onClose() {
    setShowingPicker(false)
  }
  // Scope the "esc" hotkey to the picker, so that the hotkey doesn't propagate.
  const { enableScope, disableScope } = useHotkeysContext()
  useHotkeys("esc", onClose, { scopes: ["picker"] })
  useEffect(() => {
    showingPicker ? enableScope("picker") : disableScope("picker")
  }, [showingPicker])

  const randomResourceCover = useRandomThemeResourceImage()

  useEffect(() => {
    onSelectResourceCoverImage(randomResourceCover)
  }, [randomResourceCover])

  const useResourcesQuery = () => {
    const resourcesQuery = _useResourcesQuery({
      variables: {
        search: debouncedSearch,
        resourceTypes: { eq: resourceType }
      }
    })

    const { data, error, loading } = resourcesQuery

    const { loadMore, hasMore, loadingMore } = usePaginator({
      query: resourcesQuery,
      paginatorValue: resourcesQuery.data?.resources
    })

    const resources = (
      (flattenEdges(data?.resources) || []) as ResourceFragment[]
    ).sort(
      // the closer to the beggining of the resource.name that the debouncedSearch matches,
      // the closer to the begging of the list the resource should be moved
      (resourceA, resourceB) =>
        // check against current query search variable
        resourceA.name.indexOf(resourcesQuery.variables?.search || "") -
        resourceB.name.indexOf(resourcesQuery.variables?.search || "")
    )

    return {
      resourcesQuery,
      pickerItems: resources,
      loading,
      loadingMore,
      loadMore,
      hasMore
    }
  }

  const {
    resourcesQuery,
    pickerItems: resources,
    loading
  } = useResourcesQuery()
  const totalResources = resourcesQuery.data?.resources?.total

  const isSearching = showingPicker && (debouncingSearch || loading)

  useEffect(() => {
    inputRef.current?.focus()
  }, [showingPicker])

  useDidMountEffect(() => {
    onChangeResourceName(search)
    if (selectedResource && selectedResource.name !== search)
      onSelectResource(null)
  }, [search])

  useEffect(() => {
    if (selectedResource) handleDebounceSearch(selectedResource.name)
  }, [selectedResource])

  const handleSelectResource = (resource: ResourceFragment) => {
    onSelectResource(resource)
    setShowingPicker(false)
  }

  // Closes picker and auto focuses cohort name input.
  function handleFocusCohortNameInput() {
    setShowingPicker(false)
    onFocusCohortNameInput()
  }

  const coverImage = selectedResource
    ? selectedResource.image
    : randomResourceCover

  return (
    <ResourcePickerContainer>
      <PickerUI.Row style={{ position: "relative", overflow: "visible" }}>
        <PickerUI.Icon>
          <Image
            src={coverImage}
            _loading={{
              width: "100%",
              opacity: 1,
              backgroundColor: TERTIARY_GRAY
            }}
          />
        </PickerUI.Icon>
        {preSelectedResource ? (
          <Text.H5>{preSelectedResource.name}</Text.H5>
        ) : (
          <InputGroup
            startElement={
              // Only show spinner when searching.
              showingPicker &&
              (isSearching ? (
                <Spinner size="sm" />
              ) : (
                <Pathicon size="1.1em" icon="search-list" />
              ))
            }
            endElement={
              <Button
                ref={setInputRightElementNodeRef}
                onClick={toggleShowingPicker}
                width="auto"
                justifyContent="flex-end"
                cursor="pointer"
                variant="subtle"
                m="0 4px"
                size="sm"
                pos="relative"
              >
                <HStack p="0 6px" justifyContent="space-between" w="100%">
                  {!showingPicker && (
                    <Pathicon size="1.1em" icon="search-list" />
                  )}
                  {/* Only show total resources count when > 0 */}
                  {!showingPicker && (
                    <>
                      <CharkaText as="span" opacity="1">
                        Select...
                      </CharkaText>
                      {!!totalResources && (
                        <Badge
                          pos="absolute"
                          top="-3px"
                          right="-6px"
                          borderRadius="4px"
                          border="1px solid white"
                          fontSize=".95em"
                          color="gray.600"
                          bg="gray.300"
                        >
                          {totalResources}
                        </Badge>
                      )}
                    </>
                  )}

                  {/* Only show show picker "x" when picking. */}
                  {showingPicker && <Pathicon size="1.1em" icon="x" />}
                </HStack>
              </Button>
            }
          >
            <Input
              ref={inputRef}
              type="text"
              name="path name"
              value={search}
              autoFocus={!selectedResource}
              onChange={(e) => handleDebounceSearch(e.target.value)}
              className="ResourcePicker_Input"
              data-testid="resource-picker-input"
              placeholder={
                showingPicker
                  ? t("path.new_path_form.existing", { count: totalResources })
                  : t("path.new_path_form.new")
              }
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 250)}
              onKeyDown={(e) => {
                // When user hits enter, either select existing resource when
                // showing picker, or advance to cohort input when 'search' value exists.
                if (e.keyCode === 13) {
                  if (showingPicker && resources && resources.length) {
                    handleSelectResource(resources[0])
                  } else if (search) {
                    handleFocusCohortNameInput()
                  }
                }
                // Ideally this would be handled by useHotkeys but for some reason it's not
                // when escape is keyed while input is focused.
                if (e.key === "Escape") {
                  setShowingPicker(false)
                }
              }}
              pr={`${inputRightElementWidth}px`}
              backgroundColor={showingPicker ? "gray.100" : "white.400"}
              css={{
                // NOTE: the transition isn't applying. No idea why.
                transition: "background-color .3s ease"
              }}
            />
          </InputGroup>
        )}
        {showingPicker && (
          // TODO: handle up and down arrows to highlight items (and then select with enter keypress)
          <PickerDropdown
            prompt={null}
            searchPrompt={null}
            usePickerQuery={useResourcesQuery}
            onPick={handleSelectResource}
          >
            {({ pickerList }: { pickerList: ResourceFragment[] }) => (
              <>
                {pickerList || (
                  <Box p={4} textAlign="center">
                    <Text as="span">
                      {search
                        ? t("path.new_path_form.no_search_results", { search })
                        : t("path.new_path_form.no_results")}{" "}
                      <Link
                        variant="underline"
                        onClick={handleFocusCohortNameInput}
                      >
                        {t("path.new_path_form.no_results_prompt")}
                      </Link>
                    </Text>
                  </Box>
                )}
                <ResourcePickerLicensor onOpenStore={onOpenStore} />
              </>
            )}
          </PickerDropdown>
        )}
      </PickerUI.Row>
    </ResourcePickerContainer>
  )
}

ResourcePicker.displayName = "ResourcePicker"

const ResourcePickerContainer = styled.div`
  width: 100%;

  .TextInput {
    flex-grow: 1;
  }

  /* highlight the first result to indicate selectable */
  .PickerList .PickerListItem:first-child {
    background: var(--item-background-color);
  }

  /* remove highlight of the first result when hovering over the results list (but not over the highlighted item) */
  @media (hover: hover) {
    .Picker:hover .PickerList .PickerListItem:first-child:not(:hover) {
      background: initial;
    }
  }
`

export default ResourcePicker
